import {
  Callout,
  DATE_FORMAT,
  ListBox,
  Tooltip,
  HighlightIcon,
  Button,
  SkeletonText,
  colors,
  Icon,
  Badge,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import { type ReactNode } from 'react';
import { useHistory } from 'react-router-dom';

import { EllipsisTooltip } from 'common/components/EllipsisTooltip';
import { SupplierLogo } from 'common/components/SupplierLogo';
import { ErrorBoundary } from 'common/components/withErrorBoundary';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useCompanyCurrency } from 'modules/app/hooks/useCompanyCurrency';
import {
  getCardLoadActivityText,
  getCardLoadActivityType,
  getIconPropertiesForCardActivity,
} from 'modules/cards/components/activity/helpers';
import {
  useCardAggregateActivityQuery,
  type Payment,
  type Activity,
  type FailedTransaction,
  type PhysicalCardLoad,
} from 'modules/cards/hooks/useCardAggregateActivity';
import { getCardActivityTypeTranslation } from 'modules/cards/models/get-card-activity-type-translation';
import { getDeclinedReasonDescriptionTranslationKey } from 'modules/cards/models/get-declined-reason-translation-keys';
import { formatMoneyWithPrecision2 } from 'modules/subscriptions/models/models';
import type { CurrenciesKey } from 'src/core/config/money';
import { routeFor, routes } from 'src/core/constants/routes';
import { type AnalyticEventName, track } from 'src/core/utils/analytics';

import style from './CardsPanelActivity.module.css';

type Props = {
  cardId: string;
  clickEventName?: AnalyticEventName;
  getCardTransactionDetailsTargetUrl?: (paymentId: string) => string;
  title?: ReactNode;
  selectedTransactionId?: string;
  isSubscription?: boolean;
  isLarge?: boolean;
};

export const CardsPanelActivity = ({
  cardId,
  clickEventName,
  getCardTransactionDetailsTargetUrl,
  title,
  selectedTransactionId,
  isSubscription,
  isLarge,
}: Props) => {
  const { t } = useTranslation('global');

  const company = useCompany();
  const history = useHistory();
  const companyCurrency = useCompanyCurrency();
  const cardActivityQueryState = useCardAggregateActivityQuery(cardId);

  if (cardActivityQueryState.status === 'error') {
    return <Callout title={t('misc.errors.networkError')} variant="alert" />;
  }
  if (cardActivityQueryState.status === 'loading') {
    return (
      <div className="flex flex-col items-center gap-24">
        <SkeletonText size="l" />
        <div className="separator" />
        <SkeletonText size="l" />
        <div className="separator" />
        <SkeletonText size="l" />
      </div>
    );
  }

  const activities = cardActivityQueryState.data;

  if (activities.length === 0) {
    return (
      <div className="text-center text-content-secondary-bg-primary body-m">
        {t('cards.activity.noActivityYet')}
      </div>
    );
  }

  return (
    <>
      {title ?? (
        <p className="mt-24 pl-16 body-l">{t('cards.activity.title')}</p>
      )}
      <ListBox
        className={classNames(
          style.CardsPanelActivityList,
          'shadow-none border-0',
        )}
        getIsOptionActive={(option) =>
          !!selectedTransactionId && option.id === selectedTransactionId
        }
        getOptionId={(option) => option.id}
        options={activities}
        onOptionClick={(option) => {
          if (option.aggregate_type === 'payment') {
            if (clickEventName) {
              track(clickEventName, {
                destination: 'payment page',
              });
            }
            history.push(
              routeFor(routes.PAYMENTS_ALL.path, {
                company: company.id,
                id: option.id,
              }),
            );
          } else if (
            option.aggregate_type === 'failed_transaction' &&
            getCardTransactionDetailsTargetUrl
          ) {
            history.push(getCardTransactionDetailsTargetUrl(option.id));
          }
        }}
        footer={
          cardActivityQueryState.hasNextPage && (
            <Button
              text={t('misc.loadMore')}
              variant="secondaryNeutral"
              className="mt-8"
              onClick={async () => {
                await cardActivityQueryState.fetchNextPage();
              }}
            />
          )
        }
      >
        {(activity) => {
          const activityType = activity.aggregate_type;
          return (
            <ErrorBoundary
              fallbackComponent={
                <Callout
                  variant="alert"
                  title={t('errors:unexpectedError.title')}
                />
              }
              key={activity.id}
              context={{
                team: 'cards-ux',
                scope: 'cards::panel::activity-tab',
              }}
            >
              {activityType === 'payment' && (
                <PaymentRow
                  key={activity.id}
                  payment={activity}
                  companyCurrency={companyCurrency}
                />
              )}
              {activityType === 'failed_transaction' && (
                <DeclinedPaymentRow
                  companyCurrency={companyCurrency}
                  isClickable={Boolean(getCardTransactionDetailsTargetUrl)}
                  key={activity.id}
                  payment={activity}
                />
              )}
              {activityType === 'physical_load' && (
                <CardLoadRow
                  companyCurrency={companyCurrency}
                  key={activity.id}
                  cardLoad={activity}
                  isLarge={isLarge}
                />
              )}
              {activityType === 'activity' && (
                <ActivityRow
                  key={activity.id}
                  activity={activity}
                  isSubscription={isSubscription}
                />
              )}
            </ErrorBoundary>
          );
        }}
      </ListBox>
    </>
  );
};

const ActivityRow = ({
  activity,
  isSubscription,
}: {
  activity: Activity;
  isSubscription?: boolean;
}) => {
  const { t, localeFormat } = useTranslation('global');
  const activityTitle = getCardActivityTypeTranslation(
    activity,
    t,
    isSubscription,
  );
  const iconProperties = getIconPropertiesForCardActivity({
    activityType: activity.activity_type,
    isSubscription,
  });
  return (
    <div className="flex items-center gap-16">
      {iconProperties && (
        <HighlightIcon
          name={iconProperties.icon}
          variant={iconProperties.variant}
          size={32}
        />
      )}
      <div className="flex grow flex-col">
        <span className="text-content-primary body-m">{activityTitle}</span>
      </div>
      <div className="text-right">
        <div className="text-content-secondary-bg-primary body-s">
          {localeFormat(parseISO(activity.created_at), DATE_FORMAT.MEDIUM)}
        </div>
      </div>
    </div>
  );
};

const CardLoadRow = ({
  cardLoad,
  companyCurrency,
  isLarge,
}: {
  cardLoad: PhysicalCardLoad;
  companyCurrency: CurrenciesKey;
  isLarge?: boolean;
}) => {
  const { activeLanguage, localeFormat, t } = useTranslation('global');
  const activityType = getCardLoadActivityType(cardLoad);
  const iconProperties = getIconPropertiesForCardActivity({
    activityType,
  });

  return (
    <div className="flex items-center gap-16">
      {iconProperties && (
        <HighlightIcon
          name={iconProperties.icon}
          variant={iconProperties.variant}
          size={32}
        />
      )}
      <div className="flex grow flex-col self-start">
        <div
          className={classNames(
            isLarge ? 'max-w-[800px]' : 'max-w-[300px]',
            'text-content-primary body-m',
          )}
        >
          <EllipsisTooltip
            text={getCardLoadActivityText(t, {
              cardLoad,
              cardLoadActivityType: activityType,
            })}
            data-mask
          />
        </div>
        <div className="gap-2 mt-4 flex flex-wrap items-center">
          {cardLoad.available_before && cardLoad.available_after && (
            <>
              <Badge variant="secondary">
                {formatMoneyWithPrecision2(
                  cardLoad.available_before,
                  companyCurrency,
                  activeLanguage,
                )}
              </Badge>
              <Icon name="arrow-right" color={colors.contentDecorativeIcon} />
              <Badge variant="primary">
                {formatMoneyWithPrecision2(
                  cardLoad.available_after,
                  companyCurrency,
                  activeLanguage,
                )}
              </Badge>
            </>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className="text-right text-content-primary title-m">
          {cardLoad.amount
            ? formatMoneyWithPrecision2(
                cardLoad.amount,
                companyCurrency,
                activeLanguage,
              )
            : t('misc.na')}
        </div>
        <div className="text-content-secondary-bg-primary body-s">
          {localeFormat(parseISO(cardLoad.created_at), DATE_FORMAT.MEDIUM)}
        </div>
      </div>
    </div>
  );
};

const DeclinedPaymentRow = ({
  companyCurrency,
  isClickable,
  payment,
}: {
  companyCurrency: CurrenciesKey;
  isClickable: boolean;
  payment: FailedTransaction;
}) => {
  const { t, activeLanguage, localeFormat } = useTranslation('global');

  let tooltipContent;
  if (payment.currency_billed !== payment.currency_declared) {
    const fxFeeAmount = formatMoneyWithPrecision2(
      payment.fx_fee_amount,
      companyCurrency,
      activeLanguage,
    );
    tooltipContent = t('misc.includingConversionFee', {
      amount: fxFeeAmount,
    });
  }

  return (
    <div
      className={classNames('flex items-center gap-16', {
        [style.CardsPanelActivity__clickableRow]: isClickable,
      })}
    >
      <SupplierLogo
        name={payment.supplier?.name}
        src={payment.supplier?.url ?? undefined}
      />
      <div className="flex grow flex-col self-start">
        <span className="text-content-primary body-m">
          {payment.supplier?.name ?? payment.merchant_name ?? t('misc.na')}
        </span>
        <span className="flex items-center">
          <span className="text-content-alert-default body-s">
            {t(getDeclinedReasonDescriptionTranslationKey(payment.reason_code))}
          </span>
        </span>
      </div>
      <div className="shrink-0 text-right">
        <Tooltip content={tooltipContent}>
          <div className="text-right text-content-secondary-bg-primary line-through title-m">
            {payment.currency_billed !== payment.currency_declared ? (
              <>
                {formatMoneyWithPrecision2(
                  payment.amount_declared,
                  payment.currency_declared,
                  activeLanguage,
                )}
                {` | `}
              </>
            ) : null}
            {formatMoneyWithPrecision2(
              payment.total_amount ?? payment.amount_billed,
              payment.currency_billed,
              activeLanguage,
            )}
          </div>
        </Tooltip>
        <div className="text-content-secondary-bg-primary body-s">
          {localeFormat(parseISO(payment.created_at), DATE_FORMAT.MEDIUM)}
        </div>
      </div>
    </div>
  );
};

const PaymentRow = ({
  companyCurrency,
  payment,
}: {
  companyCurrency: CurrenciesKey;
  payment: Payment;
}) => {
  const { t, activeLanguage, localeFormat } = useTranslation('global');

  let tooltipContent;
  if (payment.currency_billed !== payment.currency_declared) {
    const fxFeeAmount = formatMoneyWithPrecision2(
      payment.fx_fee_amount,
      companyCurrency,
      activeLanguage,
    );
    tooltipContent =
      payment.particularity === 'atm_withdrawal'
        ? t('misc.includingTransactionFee', {
            amount: fxFeeAmount,
          })
        : t('misc.includingConversionFee', {
            amount: fxFeeAmount,
          });
  }
  return (
    <div
      className={classNames(
        style.CardsPanelActivity__clickableRow,
        'flex items-center gap-16',
      )}
    >
      <SupplierLogo
        name={payment.supplier?.name}
        src={payment.supplier?.url ?? undefined}
      />
      <div className="flex grow flex-col self-start">
        <span className="text-content-primary body-m">
          {payment.supplier?.name || t('misc.na')}
        </span>
        <span className="text-content-secondary-bg-primary">{t('misc.payment')}</span>
      </div>
      <div className="text-right">
        <Tooltip content={tooltipContent}>
          <div className="text-right text-content-primary title-m">
            {payment.currency_billed !== payment.currency_declared ? (
              <span className="text-content-primary body-m">
                {formatMoneyWithPrecision2(
                  payment.amount_declared,
                  payment.currency_declared,
                  activeLanguage,
                )}
                {` | `}
              </span>
            ) : null}
            {formatMoneyWithPrecision2(
              payment.total_amount ?? payment.amount_billed,
              payment.currency_billed,
              activeLanguage,
            )}
          </div>
        </Tooltip>
        <div className="text-content-secondary-bg-primary body-s">
          {localeFormat(
            parseISO(payment.paid_at ?? payment.created_at),
            DATE_FORMAT.MEDIUM,
          )}
        </div>
      </div>
    </div>
  );
};
